import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import {
  QueryClient,
  QueryClientProvider,
  QueryCache,
  MutationCache,
} from '@tanstack/react-query';
import { persistQueryClient } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import './styles/globals.css';
import '@nike/eds/dist/index.css';
import './index.css';
import toast, { Toaster } from 'react-hot-toast';
import App from './App';
import RootLayout, { authLoader } from './components/layout';
import Playground from './pages/playground';
import Tokenizer from './pages/playground/tokenizer';
import ProductWall from './pages/playground/productWall';
import NewProductWall from './pages/playground/newProductWall';
import Dictionaries from './pages/dictionaries';
import NikeCustomDictionary from './pages/dictionaries/nike-custom';
import NOGODictionary from './pages/dictionaries/no-go';
import StopWordsDictionary from './pages/dictionaries/stop-words';
import ErrorPage, { ErrorBoundaryElement } from 'pages/error';
import LoginPage from 'pages/auth/login';
import LoginCallback from 'pages/auth/callback';
import LabellingDataset from './pages/labelling/datasets';
import LabellingProduct from './pages/labelling/product';
import LabellingProductItem from './pages/labelling/productItem';
import LabellingReport from './pages/labelling/report';
import NicknameProductList from './pages/nickname/productList';
import NicknameDictionary from './pages/nickname/dictionary';
import HotSearchRankingLists from 'pages/hotsearch/rankingLists';
import RewriteList from './pages/rewrite';
import RecommendConcepts from './pages/recommendConcepts';
import Ranking from './pages/ranking';
import RankingRuleCreate from './pages/ranking/createRankingRule';
import RankingRuleEdit from './pages/ranking/editRankingRule';
import ReleaseLog from './pages/releaseLog';

import { QUERY_KEYS, JWT_EXPIRATION_TIME_MS } from './constants';

import reportWebVitals from './reportWebVitals';
import { UserList } from 'pages/identity/UserList';
import { RoleList } from 'pages/identity/RoleList';
import { AuditLog } from 'pages/identity/AuditLog';
import OpenSearchIndexList from './pages/searchops/indexList';
import QueryConsole from './pages/searchops/queryConsole';
import ForceUpdate from './pages/searchops/forceUpdate';

let showSessionMsg = false;
const ToastBody = () => (
  <div className='eds-snack__body'>
    <p>
      Your session was expired, please either refresh the page or{' '}
      <a href='/login' className='eds-link'>
        login
      </a>
    </p>
  </div>
);
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onSuccess: () => {
      showSessionMsg = false;
    },
    onError: (error) => {
      console.log(error);
      const err = error as { status: number };
      if (err.status === 401 && !showSessionMsg) {
        showSessionMsg = true;
        toast.error(<ToastBody />, {
          duration: 1000 * 60 * 60 * 24,
        });
      }
    },
  }),
  mutationCache: new MutationCache({
    onSuccess: () => {
      showSessionMsg = false;
    },
    onError: (error) => {
      console.log(error);
      const err = error as { status: number };
      if (err.status === 401 && !showSessionMsg) {
        showSessionMsg = true;
        toast.error(<ToastBody />, {
          duration: 1000 * 60 * 60 * 24,
        });
      }
    },
  }),
});
const localStoragePersister = createSyncStoragePersister({
  storage: window.localStorage,
});

persistQueryClient({
  queryClient,
  persister: localStoragePersister,
  maxAge: JWT_EXPIRATION_TIME_MS, // equal to or less than the token expiration time
  dehydrateOptions: {
    shouldDehydrateQuery: ({ queryKey }) => {
      return queryKey[0] === QUERY_KEYS.ACCESS_TOKEN;
    },
  },
});

const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <ErrorPage />,
    loader: authLoader(queryClient),
    children: [
      {
        index: true,
        element: <App />,
        loader: authLoader(queryClient),
        errorElement: <ErrorBoundaryElement />,
      },
      {
        path: 'playground',
        element: <Playground />,
        errorElement: <ErrorBoundaryElement />,
      },
      {
        path: 'playground/tokenizer',
        element: <Tokenizer />,
        loader: authLoader(queryClient),
        errorElement: <ErrorBoundaryElement />,
      },
      {
        path: 'playground/product-wall',
        element: <ProductWall />,
        loader: authLoader(queryClient),
        errorElement: <ErrorBoundaryElement />,
      },
      {
        path: 'playground/new-product-wall',
        element: <NewProductWall />,
        loader: authLoader(queryClient),
        errorElement: <ErrorBoundaryElement />,
      },
      {
        path: 'dictionaries',
        element: <Dictionaries />,
        loader: authLoader(queryClient),
        errorElement: <ErrorBoundaryElement />,
      },
      {
        path: 'dictionaries/nike-custom',
        element: <NikeCustomDictionary />,
        loader: authLoader(queryClient),
        errorElement: <ErrorBoundaryElement />,
      },
      {
        path: 'dictionaries/no-go',
        element: <NOGODictionary />,
        loader: authLoader(queryClient),
        errorElement: <ErrorBoundaryElement />,
      },
      {
        path: 'dictionaries/stop-words',
        element: <StopWordsDictionary />,
        loader: authLoader(queryClient),
        errorElement: <ErrorBoundaryElement />,
      },
      {
        path: 'labelling/datasets',
        element: <LabellingDataset />,
        loader: authLoader(queryClient),
        errorElement: <ErrorBoundaryElement />,
      },
      {
        path: 'labelling/datasets/:datasetId/products',
        element: <LabellingProduct />,
        loader: authLoader(queryClient),
        errorElement: <ErrorBoundaryElement />,
        children: [
          {
            path: ':productId',
            element: <LabellingProductItem />,
            loader: authLoader(queryClient),
          },
        ],
      },
      {
        path: 'labelling/datasets/:datasetId/report',
        element: <LabellingReport />,
        loader: authLoader(queryClient),
        errorElement: <ErrorBoundaryElement />,
      },
      {
        path: 'nickname/list',
        element: <NicknameProductList />,
        loader: authLoader(queryClient),
        errorElement: <ErrorBoundaryElement />,
      },
      {
        path: 'nickname/dictionary',
        element: <NicknameDictionary />,
        loader: authLoader(queryClient),
        errorElement: <ErrorBoundaryElement />,
      },
      {
        path: 'hotsearch/ranking-lists',
        element: <HotSearchRankingLists />,
        loader: authLoader(queryClient),
        errorElement: <ErrorBoundaryElement />,
      },
      {
        path: 'rewrite/list',
        element: <RewriteList />,
        loader: authLoader(queryClient),
        errorElement: <ErrorBoundaryElement />,
      },
      {
        path: 'recommend-concepts',
        element: <RecommendConcepts />,
        loader: authLoader(queryClient),
        errorElement: <ErrorBoundaryElement />,
      },
      {
        path: 'ranking',
        element: <Ranking />,
        loader: authLoader(queryClient),
        errorElement: <ErrorBoundaryElement />,
      },
      {
        path: 'ranking/create-rule',
        element: <RankingRuleCreate />,
        loader: authLoader(queryClient),
        errorElement: <ErrorBoundaryElement />,
      },
      {
        path: 'ranking/edit-rule/:ruleId',
        element: <RankingRuleEdit />,
        loader: authLoader(queryClient),
        errorElement: <ErrorBoundaryElement />,
      },
      {
        path: '/identity',
        element: <UserList />,
        loader: authLoader(queryClient),
        errorElement: <ErrorBoundaryElement />,
      },
      {
        path: '/identity/users',
        element: <UserList />,
        loader: authLoader(queryClient),
        errorElement: <ErrorBoundaryElement />,
      },
      {
        path: '/identity/roles',
        element: <RoleList />,
        loader: authLoader(queryClient),
        errorElement: <ErrorBoundaryElement />,
      },
      {
        path: '/identity/audit',
        element: <AuditLog />,
        loader: authLoader(queryClient),
        errorElement: <ErrorBoundaryElement />,
      },
      {
        path: '/searchops/indexes',
        element: <OpenSearchIndexList />,
        loader: authLoader(queryClient),
        errorElement: <ErrorBoundaryElement />,
      },
      {
        path: '/searchops/query-console',
        element: <QueryConsole />,
        loader: authLoader(queryClient),
        errorElement: <ErrorBoundaryElement />,
      },
      {
        path: '/searchops/force-update',
        element: <ForceUpdate />,
        loader: authLoader(queryClient),
        errorElement: <ErrorBoundaryElement />,
      },
      {
        path: 'release-log',
        element: <ReleaseLog />,
        loader: authLoader(queryClient),
        errorElement: <ErrorBoundaryElement />,
      },
    ],
  },
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/api/auth/callback/okta',
    element: <LoginCallback />,
  },
]);

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
);
root.render(
  // <StrictMode>
  <QueryClientProvider client={queryClient}>
    <RouterProvider router={router} />
    <Toaster />
    <ReactQueryDevtools initialIsOpen={false} />
  </QueryClientProvider>,
  // </StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
